import React from 'react';
import PageMeta from '../components/PageMeta';

const pillars = [
  {
    title: 'Structured Intelligence',
    text: 'We turn noisy markets, legal developments and policy shifts into clear frameworks that can be studied, compared and revisited.',
  },
  {
    title: 'Research Discipline',
    text: 'Our work emphasizes source-backed analysis, transparent assumptions and practical context over fast commentary.',
  },
  {
    title: 'Learning Depth',
    text: 'BTree is built for professionals, students and aspirants who want to understand the why behind the numbers, rulings and reforms.',
  },
];

const coverage = [
  'Equity research and market intelligence',
  'Legal judgments, doctrine and regulatory analysis',
  'Finance education, valuation and professional learning',
  'UPSC-oriented current affairs and public policy context',
];

const AboutUs = () => {
  return (
    <>
      <PageMeta
        title="About The B Tree | Research, Law, Markets & Policy Intelligence"
        description="Learn how The B Tree turns complex markets, legal developments, finance education, and policy shifts into structured intelligence."
        canonical="https://www.thebtree.com/about"
        image="https://www.thebtree.com/logo.png"
        type="website"
      />
      <main className="min-h-screen bg-[#F7F5F2]" data-testid="about-page">
      <section className="bg-gradient-to-b from-[#FDFCFB] to-[#F7F5F2] border-b border-[#C8A84B]/20">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-14 md:py-18">
          <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] font-semibold mb-3">
            About The BTree Company
          </p>
          <h1 className="text-4xl md:text-6xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-[1.05] mb-5">
            Intelligence for markets, law, learning and policy.
          </h1>
          <p className="text-lg text-slate-700 leading-8 max-w-3xl">
            BTree is not a news platform. It is a structured intelligence system designed to simplify complex
            markets, legal developments, finance education and public policy.
          </p>
          <p className="text-lg text-slate-700 leading-8 max-w-3xl mt-5">
            Welcome to The Banana Tree! While our full name represents our deep roots and big ideas, we usually
            go by thebtree. It is short, sweet, and exactly how our friends and clients know us.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {pillars.map((pillar) => (
            <article key={pillar.title} className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
              <h2 className="text-2xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] mb-3">
                {pillar.title}
              </h2>
              <p className="text-slate-700 leading-7">
                {pillar.text}
              </p>
            </article>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10 shadow-sm">
            <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] font-semibold mb-3">
              Our Approach
            </p>
            <h2 className="text-3xl md:text-4xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] mb-4">
              Built for clarity without flattening complexity.
            </h2>
            <p className="text-slate-700 leading-8 mb-5">
              We serve investors, students, UPSC aspirants and legal professionals who need institutional-grade
              analysis without unnecessary noise. The goal is to make difficult subjects easier to reason through,
              not to reduce them to headlines.
            </p>
            <p className="text-slate-700 leading-8">
              Each BTree vertical is designed around structured research, repeatable frameworks and careful
              explanation, so users can move from awareness to understanding with confidence.
            </p>
          </article>

          <article className="bg-[#0D1B2A] text-white rounded-2xl p-6 md:p-10 shadow-sm">
            <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] font-semibold mb-4">
              What We Cover
            </p>
            <ul className="space-y-4">
              {coverage.map((item) => (
                <li key={item} className="flex gap-3 text-slate-100 leading-7">
                  <span className="mt-3 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#C8A84B]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </section>
    </main>
    </>
  );
};

export default AboutUs;
